/**
 * Product configuration.
 *
 * Brand strings, thresholds and feature flags live here rather than being
 * scattered through components, so that renaming the product or changing a
 * pricing tier is a one-file change.
 */

export const BRAND = {
  /** The visible product name. Never hyphenated (§ closing note of the brief). */
  name: 'Yuan Math',
  domain: 'Yuan-Math.com',
  tagline: 'Master Mathematics, Step by Step.',
  heroHeadline: 'Mathematics from Kindergarten to University.',
  heroSubheadline:
    'Learn mathematics step by step, understand why it works, and build mastery that lasts.',
} as const;

/**
 * Feature flags.
 *
 * The AI tutor is deliberately off: the design document is explicit that the
 * deterministic learning system must work first (§13). The flag exists so the
 * surrounding UI can be built and reviewed without shipping the feature.
 */
export const FEATURES = {
  aiTutor: false,
  parentDashboard: true,
  teacherDashboard: true,
  competitions: false,
  /**
   * The adventure (docs/rpg.md). Shipped dark and built up PR by PR behind
   * this flag; PR 14 is the one that flipped it on.
   *
   * It stays a flag rather than becoming the absence of one, because it is
   * still the switch that turns ten kingdoms, their quest lines and bosses, and
   * every `/adventure` route off in one edit — and because nothing may depend on it
   * being true: the curriculum, the Math Map, courses, lessons and practice are
   * all first-class without it, and `adventureFlag.test.tsx` pins that by
   * turning it back off.
   */
  rpg: true,
  /**
   * Payments: Stripe Checkout, the billing portal, the pricing page and the
   * free tier's daily practice cap.
   *
   * Off while the platform is being shown to people for the first time. Nothing
   * about the payment system is deleted — `PricingPage`, `state/billing.ts`,
   * `packages/promo` and the four Edge Functions are all intact — because the
   * switch has to be reversible in one edit, and a system re-typed from comments
   * six months later is not the system that was tested.
   *
   * Turning it off **lifts the free cap** rather than merely hiding the way to
   * pay: `FREE.dailyProblemLimit` is 30 questions a day, and a visitor who hits
   * that with no pricing page to go to has been stopped by a paywall with no
   * wall behind it. `LearnerContext` is where that happens, so the tier table in
   * `packages/learning` stays a pure description of what a tier grants.
   *
   * Nothing may depend on this being true — the curriculum, lessons, the Math
   * Map and practice are all first-class without it, and `billing.test.tsx`
   * pins the boundary from both sides.
   */
  payments: false,
} as const;

/**
 * How often a subscriber is billed.
 *
 * An interval, deliberately **not** a tier. Annual Premium and monthly Premium
 * entitle exactly the same thing, so `Tier` stays four values and
 * `entitlementsFor()` never has to ask how somebody pays. Adding
 * `premium-annual` to that union would fork the entitlement table in two and
 * every lookup would have to remember both halves.
 */
export type BillingInterval = 'month' | 'year';

/** Two months free for paying up front — the discount is the whole pitch. */
export const ANNUAL_MONTHS_CHARGED = 10;

export const PRICING = {
  free: {
    name: 'Free',
    price: 'Free',
    period: 'forever',
    description: 'Enough to learn real mathematics, with no card and no trial clock.',
    features: [
      'Every lesson in the published curriculum',
      'Up to 30 practice problems a day',
      'The full Math Map',
      'Mastery tracking and streaks',
      'A daily challenge',
    ],
    cta: 'Start learning',
  },
  premium: {
    name: 'Premium',
    price: '$12',
    period: 'per month',
    annual: { price: '$120', period: 'per year', monthly: '$10' },
    description: 'For a student working through mathematics seriously.',
    features: [
      'Unlimited practice',
      'Adaptive review scheduling',
      'Full diagnostic assessments',
      'Detailed progress and mistake analysis',
      'Advanced and competition courses as they launch',
      'AI tutor when it arrives',
    ],
    cta: 'Go Premium',
    highlighted: true,
  },
  family: {
    name: 'Family',
    price: '$20',
    period: 'per month',
    annual: { price: '$200', period: 'per year', monthly: '$16.67' },
    description: 'Up to five learners, with a parent dashboard.',
    features: [
      'Everything in Premium, for five children',
      'Parent dashboard and weekly summaries',
      'Per-child privacy controls',
      'No advertising, ever',
    ],
    cta: 'Choose Family',
  },
  school: {
    name: 'School',
    price: 'Custom',
    period: 'per class',
    description: 'For classrooms and institutions.',
    features: [
      'Class creation and roster management',
      'Assignments and reporting',
      'Struggling-student alerts',
      'Curriculum alignment support',
    ],
    cta: 'Contact us',
  },
} as const;

/** A plan as the pricing page needs to show it, at one billing interval. */
export type DisplayPrice = {
  price: string;
  period: string;
  /** The per-month equivalent of an annual plan, or undefined. */
  monthly?: string;
  /** What paying up front saves, or undefined when there is nothing to save. */
  saving?: string;
};

/**
 * What one plan costs at one interval.
 *
 * Here rather than in the pricing page because a price shown that a checkout
 * does not charge is the worst kind of bug in this area — one file decides
 * what a plan costs, and the page only lays it out. Free and School have no
 * annual variant and quietly keep their single price; neither is a monthly
 * charge, so there is nothing to discount and no toggle state to explain.
 */
export function displayPrice(
  plan: (typeof PRICING)[keyof typeof PRICING],
  interval: BillingInterval,
): DisplayPrice {
  const annual = 'annual' in plan ? plan.annual : undefined;
  if (interval === 'month' || !annual) return { price: plan.price, period: plan.period };

  return {
    price: annual.price,
    period: annual.period,
    monthly: annual.monthly,
    saving: `${12 - ANNUAL_MONTHS_CHARGED} months free`,
  };
}

/** Whether this plan can be bought by the year at all. */
export function hasAnnual(plan: (typeof PRICING)[keyof typeof PRICING]): boolean {
  return 'annual' in plan;
}

/** Free-tier daily practice cap. Generous enough not to interrupt learning. */
export const FREE_DAILY_PROBLEM_LIMIT = 30;

export const STORAGE_KEY = 'yuan-math.profile.v1';
